import { motion } from "motion/react";
import { 
  BookOpen, 
  Check, 
  X, 
  HelpCircle, 
  Info, 
  Lightbulb, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldCheck, 
  Sparkles,
  ArrowRight,
  Target
} from "lucide-react";

interface HomepagePillarsProps {
  navigate?: (path: string) => void;
}

// Helper to format text with **bold keywords** and [links](/url)
function renderFormattedText(text: string, navigate?: (path: string) => void) {
  if (!text) return null;

  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);

  return (
    <>
      {parts.map((part, i) => {
        // Link match: [Label](/path)
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          const label = linkMatch[1];
          const url = linkMatch[2];
          return (
            <a
              key={i}
              href={url}
              onClick={(e) => {
                e.preventDefault();
                if (navigate) {
                  navigate(url);
                } else {
                  window.location.href = url;
                }
              }}
              className="font-bold text-emerald-700 dark:text-emerald-400 underline decoration-emerald-500/70 underline-offset-4 hover:text-emerald-900 dark:hover:text-emerald-200 transition-colors cursor-pointer"
            >
              {label}
            </a>
          );
        }

        // Bold match: **Bold Text**
        const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
        if (boldMatch) {
          return (
            <strong
              key={i}
              className="font-bold text-gray-900 dark:text-gray-100"
            >
              {boldMatch[1]}
            </strong>
          );
        }

        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

// Homepage Targeted Keywords Box
export function HomepageTargetedKeywordsBox() {
  const keywords = [
    { keyword: "Plant Identifier", type: "Primary Keyword" },
    { keyword: "identify plants from photo free", type: "Secondary Keyword" },
    { keyword: "AI plant identification online", type: "LSI Botanical Target" },
    { keyword: "botanical species taxonomy guide", type: "Entity Taxonomy Target" }
  ];

  return (
    <div className="bg-gradient-to-br from-emerald-50/80 via-slate-50 to-teal-50/40 border border-emerald-200/90 rounded-2xl p-4 shadow-3xs text-left space-y-3">
      <div className="flex items-start justify-between gap-3 border-b border-emerald-200/60 pb-2.5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-emerald-700 text-white rounded-lg shadow-2xs">
              <Target className="w-4 h-4" />
            </span>
            <h3 className="text-xs font-black text-gray-950 uppercase font-mono tracking-wider">
              Targeted Keywords
            </h3>
          </div>
          <p className="text-[11px] font-extrabold text-emerald-800 dark:text-emerald-300 font-mono">
            Plant Identifier Core Knowledge Hub & Diagnostic Operating System
          </p>
        </div>
        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-mono font-black rounded-full border border-emerald-200 shrink-0">
          SEO Targets
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {keywords.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-250 rounded-xl text-xs font-bold text-slate-900 shadow-3xs hover:border-emerald-300 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
            <span className="font-black text-gray-950">{item.keyword}</span>
            <span className="text-[10px] font-mono font-extrabold px-1.5 py-0.2 bg-emerald-50 text-emerald-800 rounded border border-emerald-200 ml-0.5">
              {item.type}
            </span>
          </div>
        ))}
      </div>

      <p className="text-[10px] font-semibold text-slate-500 leading-normal border-t border-slate-200/80 pt-2">
        ✦ All targeted keywords are embedded and highlighted in bold throughout the homepage educational pillars with contextual links.
      </p>
    </div>
  );
}

export default function HomepagePillars({ navigate }: HomepagePillarsProps) {
  return (
    <div className="space-y-24 py-16">
      
      {/* SECTION 1: INTRODUCTION */}
      <section id="introduction" className="scroll-mt-24 space-y-6">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200/50 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest">
          <Info className="w-3.5 h-3.5" /> Introduction
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-snug">
          Introduction to Plant Identifier
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-8 space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed font-medium">
            <p>
              {renderFormattedText(
                "Welcome to **Plant Identifier**, the internet’s premier botanical knowledge portal and instant identification workspace. Our mission is simple: to make nature accessible, educational, and engaging for everyone. When you need to **identify plants from photo free**, our platform utilizes state-of-the-art computer vision to deliver an authoritative **botanical species taxonomy guide** in under three seconds.",
                navigate
              )}
            </p>
            <p>
              {renderFormattedText(
                "By integrating **AI plant identification online** with verified taxonomic datasets, we empower gardeners, students, and researchers to explore wild flora and domestic plants with total confidence. You can quickly cross-reference scans with our specialized utilities, including our [Flower Identifier](/tools/flower-identifier), [Tree Identifier](/tools/tree-identifier), and [Weed Identifier](/tools/weed-identifier).",
                navigate
              )}
            </p>
            <p>
              {renderFormattedText(
                "Every plant profile generated by **Plant Identifier** comes backed by actionable advice from our [Botanical Database](/database) and [Plant Care Blog](/blog). Whether you are troubleshooting leaves using our [Plant Disease Identifier](/tools/disease-identifier) or calculating moisture needs with our [Watering Calculator](/tools/watering-calculator), our goal is to replace guesswork with science-backed horticultural expertise.",
                navigate
              )}
            </p>
          </div>
          <div className="md:col-span-4 bg-green-50/45 border border-green-150/50 rounded-2xl p-6 space-y-4">
            <h3 className="font-extrabold text-sm text-green-800 uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-600" /> Human-First Mission
            </h3>
            <p className="text-xs text-gray-500 font-semibold leading-relaxed">
              We design our resources for humans first, never search algorithms. Our content is crafted to be clear, trustworthy, and actionable, enabling anyone to grow healthy indoor rooms and outdoor spaces.
            </p>
            <div className="border-t border-green-150/40 pt-3">
              <span className="text-[10px] text-gray-400 font-extrabold uppercase block">Primary Goal</span>
              <span className="text-xs font-black text-green-900 block mt-0.5">Verified Science, Accessible English</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: WHAT IS AI PLANT IDENTIFICATION? */}
      <section id="what-is-ai" className="scroll-mt-24 space-y-6">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200/50 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest">
          <BookOpen className="w-3.5 h-3.5" /> Botanical Tech
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-snug">
          What is AI Plant Identification?
        </h2>
        <div className="space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed font-medium">
          <p>
            {renderFormattedText(
              "**AI plant identification online** is a revolutionary technology that leverages deep neural networks and computer vision to classify plant species from digital photographs. Instead of paging through heavy physical field guides, anyone looking to **identify plants from photo free** can upload a snapshot of a leaf, bloom, or bark to **Plant Identifier** and receive a comprehensive **botanical species taxonomy guide** instantly.",
              navigate
            )}
          </p>
          <p>
            {renderFormattedText(
              "Our core neural model analyzes key visual landmarks across leaves, stems, and blossoms. For leaf analysis, it measures blade contour, vein geometry (venation), and edge serrations. For flowers, it evaluates petal symmetry, stamen configurations, and color histograms. This multi-layered analysis feeds directly into our [Tools Hub](/tools) for deeper diagnostics.",
              navigate
            )}
          </p>
          <p>
            {renderFormattedText(
              "Visual features are mapped against over 120,000 verified species records in our system. **Plant Identifier** calculates a precise confidence score for every match. For complete garden management, pair your identification scans with our [Soil pH Analyzer](/tools/soil-ph-analyzer), [Sunlight Exposure Meter](/tools/sunlight-exposure-meter), and [Fertilization Planner](/tools/fertilization-planner).",
              navigate
            )}
          </p>
          <div className="bg-gray-55/40 border border-gray-150/60 rounded-2xl p-6.5 mt-4 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="space-y-1.5">
              <span className="text-sm font-black text-gray-900 block">Leaf Venation</span>
              <p className="text-xs text-gray-500 font-semibold leading-relaxed">
                Analyzes primary and secondary vein patterns to identify taxonomic families with high accuracy.
              </p>
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-black text-gray-900 block">Floral Morphology</span>
              <p className="text-xs text-gray-500 font-semibold leading-relaxed">
                Inspects petal arrangements, reproductive structures, and colors to differentiate rare cultivars.
              </p>
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-black text-gray-900 block">Taxonomic Alignment</span>
              <p className="text-xs text-gray-500 font-semibold leading-relaxed">
                Cross-references visual data with verified databases like Kew’s Plants of the World Online.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: HOW TO IDENTIFY A PLANT (STEP-BY-STEP) */}
      <section id="how-to-identify" className="scroll-mt-24 space-y-6">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200/50 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest">
          <Lightbulb className="w-3.5 h-3.5" /> Step-by-Step
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-snug">
          How to Identify a Plant from a Picture
        </h2>
        <p className="text-sm sm:text-base text-gray-550 font-medium">
          {renderFormattedText(
            "Getting accurate results with **Plant Identifier** is simple when capturing high-quality photos. Follow our verified 4-step framework to **identify plants from photo free** with high confidence:",
            navigate
          )}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white border border-gray-150 rounded-2xl p-6 space-y-3 relative overflow-hidden shadow-3xs">
            <span className="text-3xl font-black text-green-600/15 font-mono absolute top-4 right-4">01</span>
            <span className="text-xs font-extrabold text-green-700 uppercase tracking-wider block">PREPARE LIGHTING</span>
            <h3 className="text-base font-bold text-gray-950 tracking-tight">Ensure Good Light</h3>
            <p className="text-xs text-gray-500 font-semibold leading-relaxed">
              Take photos in bright, indirect natural sunlight so **AI plant identification online** can read exact leaf textures.
            </p>
          </div>
          <div className="bg-white border border-gray-150 rounded-2xl p-6 space-y-3 relative overflow-hidden shadow-3xs">
            <span className="text-3xl font-black text-green-600/15 font-mono absolute top-4 right-4">02</span>
            <span className="text-xs font-extrabold text-green-700 uppercase tracking-wider block">ISOLATE SUBJECT</span>
            <h3 className="text-base font-bold text-gray-950 tracking-tight">Focus on One Specimen</h3>
            <p className="text-xs text-gray-500 font-semibold leading-relaxed">
              Ensure only one plant is visible in frame. Move weeds or background debris to help **Plant Identifier** target the leaf.
            </p>
          </div>
          <div className="bg-white border border-gray-150 rounded-2xl p-6 space-y-3 relative overflow-hidden shadow-3xs">
            <span className="text-3xl font-black text-green-600/15 font-mono absolute top-4 right-4">03</span>
            <span className="text-xs font-extrabold text-green-700 uppercase tracking-wider block">MULTIPLE FEATURES</span>
            <h3 className="text-base font-bold text-gray-950 tracking-tight">Capture Key Features</h3>
            <p className="text-xs text-gray-500 font-semibold leading-relaxed">
              Capture a flat shot of a single leaf, flower, or bark for our **botanical species taxonomy guide** to evaluate.
            </p>
          </div>
          <div className="bg-white border border-gray-150 rounded-2xl p-6 space-y-3 relative overflow-hidden shadow-3xs">
            <span className="text-3xl font-black text-green-600/15 font-mono absolute top-4 right-4">04</span>
            <span className="text-xs font-extrabold text-green-700 uppercase tracking-wider block">VERIFY RESULTS</span>
            <h3 className="text-base font-bold text-gray-950 tracking-tight">Compare and Validate</h3>
            <p className="text-xs text-gray-500 font-semibold leading-relaxed">
              Cross-reference matches with our [Botanical Database](/database) and [Plant Disease Identifier](/tools/disease-identifier) for total accuracy.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: WHY CHOOSE PLANTIDENTIFER.COM */}
      <section id="why-choose-us" className="scroll-mt-24 space-y-6">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200/50 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest">
          <ShieldCheck className="w-3.5 h-3.5" /> Core Value
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-snug">
          Why Choose PlantIdentifer.com?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed font-medium">
            <p>
              {renderFormattedText(
                "Unlike basic photo tools, **PlantIdentifer.com** is a complete botanical ecosystem. We combine instant scanning with educational resources to help you **identify plants from photo free** while building long-term horticultural knowledge.",
                navigate
              )}
            </p>
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-gray-900 block text-sm">Professional Care Guides</span>
                  <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                    Receive verified cultivation rules detailing light meters, exact water volumes, and seasonal growth care steps via our [Tools Hub](/tools).
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-gray-900 block text-sm">Plant Disease Diagnostics</span>
                  <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                    Identify brown leaf margins, fungal mildews, or rust spots using our [Plant Disease Identifier](/tools/disease-identifier) and receive instant organic treatment suggestions.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-gray-900 block text-sm">Poisonous Flora Alerts</span>
                  <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                    Access immediate pet safety and human toxicity alerts from our [Botanical Database](/database) to keep kids, cats, and dogs protected.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-5 bg-gradient-to-br from-gray-950 to-slate-900 text-white rounded-2xl p-6.5 border border-slate-800 space-y-4">
            <h3 className="text-lg font-black text-green-400">Scientific Integrity</h3>
            <p className="text-xs text-gray-300 leading-relaxed font-medium">
              "We prioritize absolute botanical accuracy. Our data is reviewed by horticulturists, aligning with international standards from USDA and Kew Royal Botanic Gardens."
            </p>
            <div className="border-t border-slate-800 pt-3 flex items-center justify-between text-[10px] uppercase font-bold tracking-wider text-gray-400">
              <span>Taxonomic Standards</span>
              <span className="text-green-400">100% Compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: BENEFITS OF AI PLANT IDENTIFICATION (COMPARISON TABLE) */}
      <section id="benefits" className="scroll-mt-24 space-y-6">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200/50 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> Analytical Benefits
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-snug">
          The Benefits of AI Plant Identification
        </h2>
        <p className="text-sm sm:text-base text-gray-650 leading-relaxed font-medium">
          {renderFormattedText(
            "Using **AI plant identification online** democratizes botanical science. While physical field guides remain valuable for deep field work, **Plant Identifier** delivers instant educational context to **identify plants from photo free** during daily outdoor walks and home gardening.",
            navigate
          )}
        </p>

        {/* Dynamic Comparison Table */}
        <div className="overflow-x-auto rounded-2xl border border-gray-150 shadow-3xs bg-white">
          <table className="w-full text-left border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-150 text-[10px] font-extrabold text-gray-450 tracking-wider uppercase">
                <th className="py-4 px-6">Feature</th>
                <th className="py-4 px-6">AI Image Recognition</th>
                <th className="py-4 px-6">Manual Field Guides</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-semibold text-gray-700">
              <tr>
                <td className="py-4 px-6 font-black text-gray-950">Identification Speed</td>
                <td className="py-4 px-6 text-green-700">Instantaneous (Under 3 Seconds)</td>
                <td className="py-4 px-6 text-gray-500">Slow (Requires paging, dichotomous keys)</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-black text-gray-950">Skill Required</td>
                <td className="py-4 px-6 text-green-700">Zero (Point and shoot photo)</td>
                <td className="py-4 px-6 text-gray-500">High (Requires knowledge of botanical terms)</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-black text-gray-950">Disease Diagnosis</td>
                <td className="py-4 px-6 text-green-700">Yes (Via our [Plant Disease Identifier](/tools/disease-identifier))</td>
                <td className="py-4 px-6 text-gray-500">Limited (Requires active research/lab testing)</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-black text-gray-950">Water/Soil Planning</td>
                <td className="py-4 px-6 text-green-700">Integrated care calculators & advisors</td>
                <td className="py-4 px-6 text-gray-500">None (Requires checking secondary encyclopedias)</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-black text-gray-950">Accuracy Validation</td>
                <td className="py-4 px-6 text-green-700">Dynamic confidence rating percentages</td>
                <td className="py-4 px-6 text-green-700">Absolute (Horticulturist physical validation)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* SECTION 6: EXPERT TIPS & COMMON MISTAKES */}
      <section id="tips-mistakes" className="scroll-mt-24 space-y-6">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200/50 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest">
          <AlertTriangle className="w-3.5 h-3.5" /> Expert Advice
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-snug">
          Expert Botanical Tips & Common Mistakes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Column 1: Expert Tips */}
          <div className="bg-green-50/20 border border-green-150/50 rounded-2xl p-6.5 space-y-4">
            <h3 className="text-lg font-bold text-green-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-green-600" /> Expert Photography Tips
            </h3>
            <ul className="space-y-3 text-xs sm:text-sm text-gray-600 font-semibold leading-relaxed">
              <li className="flex gap-2 items-start">
                <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <span><strong>Capture Stem Nodes:</strong> For creeping foliage, including stem nodes helps **Plant Identifier** separate species.</span>
              </li>
              <li className="flex gap-2 items-start">
                <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <span><strong>Flatten the Leaf:</strong> Hold leaves flat against a neutral palm to help **identify plants from photo free** without blur.</span>
              </li>
              <li className="flex gap-2 items-start">
                <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <span><strong>Include Reproductive Organs:</strong> Photograph blossoms to feed our **botanical species taxonomy guide** exact petal counts.</span>
              </li>
            </ul>
          </div>

          {/* Column 2: Common Mistakes */}
          <div className="bg-red-50/20 border border-red-155/30 rounded-2xl p-6.5 space-y-4">
            <h3 className="text-lg font-bold text-red-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Common Scanning Mistakes
            </h3>
            <ul className="space-y-3 text-xs sm:text-sm text-gray-600 font-semibold leading-relaxed">
              <li className="flex gap-2 items-start">
                <X className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <span><strong>Blurry Action Shots:</strong> Shaking camera shots limit margin texture analysis during **AI plant identification online**.</span>
              </li>
              <li className="flex gap-2 items-start">
                <X className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <span><strong>Crowded Frame:</strong> Getting multiple species in one shot confuses **Plant Identifier**.</span>
              </li>
              <li className="flex gap-2 items-start">
                <X className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <span><strong>Over-Exposure:</strong> Harsh glare hides leaf vein patterns needed for an accurate scan.</span>
              </li>
            </ul>
          </div>

        </div>
      </section>

    </div>
  );
}

